// useCoordGridOverlay.js — coordinate-grid overlay (ticket 07 extraction): seeding the active
// grid from persisted meta, plus the grid-draw/throttled-moveend effect that renders the
// MGRS/UTM/WGS84 grid lines + labels on the map canvas. Composed in MapEditorModalInner after
// the map-lifecycle hook.

import { useEffect, useRef, useState } from 'react';
import {
    addCoordGridLayers,
    removeCoordGridLayers,
    updateCoordGrid,
    drawCoordGridLabels,
} from '../coordGrid';

export default function useCoordGridOverlay({ mapRef, mapReady, activeGridId }) {
    const [activeGrid, setActiveGrid] = useState(null);
    const gridCanvasRef = useRef(null);
    const gridThrottleRef = useRef(null);
    // rerender-derived-state-no-effect / 5.12: lazy-initialize activeGrid from meta so we
    // don't need a seeding effect. The ref guard is preserved for the edge case where meta
    // loads asynchronously after first render.
    const gridSeededRef = useRef(false);

    // -- Seed activeGrid from the originating node's persisted attr ----------
    // This has to stay an effect rather than derived-during-render state: once seeded,
    // `activeGrid` is locally owned UI state (the user can toggle it via the CoordGridControl
    // without necessarily persisting every intermediate toggle — see MapEditorModal's
    // `handleGridToggle`), so re-deriving from the prop on every render would clobber local
    // toggles with whatever was last persisted. The `gridSeededRef` guard (rather than a plain
    // `useState(activeGridId)` initializer) exists for the same "seed exactly once, even if the
    // prop briefly starts undefined" reason `initialMapMetaRef` in MapEditorModal.jsx documents.
    useEffect(() => {
        if (!gridSeededRef.current && activeGridId !== undefined) {
            gridSeededRef.current = true;
            setActiveGrid(activeGridId || null);
        }
    }, [activeGridId]);

    // -- Coord grid setup -----------------------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return;
        if (!activeGrid) {
            removeCoordGridLayers(map);
            const canvas = gridCanvasRef.current;
            if (canvas) { const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height); }
            return;
        }
        addCoordGridLayers(map);
        updateCoordGrid(map, activeGrid);
        drawCoordGridLabels(gridCanvasRef.current, map, activeGrid);
        const throttledUpdate = () => {
            clearTimeout(gridThrottleRef.current);
            gridThrottleRef.current = setTimeout(() => {
                updateCoordGrid(map, activeGrid);
                drawCoordGridLabels(gridCanvasRef.current, map, activeGrid);
            }, 200);
        };
        map.on('moveend', throttledUpdate);
        return () => { map.un('moveend', throttledUpdate); clearTimeout(gridThrottleRef.current); };
    }, [mapRef, activeGrid, mapReady]);

    return { activeGrid, setActiveGrid, gridCanvasRef };
}
